import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class ExtractFromJSON extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.extractfromjson',
        group: 'Data Preparation',
        title: 'Extract from JSON',
        description: `
      This component creates a new column with values extracted from the JSON strings in another column
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'json',
          type: WorkflowComponentParamType.Column,
          title: 'JSON column',
          parent: 'source',
          dataType: [ColumnDataType.String]
        },
        {
          name: 'path',
          type: WorkflowComponentParamType.String,
          title: 'JSON path'
        },
        {
          name: 'newcol',
          type: WorkflowComponentParamType.String,
          title: 'Name for new column',
          default: 'newcol',
          allowExpressions: false
        }
      ]
    )
  }

  getInnerSql({
    params,
    connection,
    componentName,
    workflow
  }: WorkflowComponentCodeParams) {
    const preparedParams = this._prepareParameters(params)
    let path = preparedParams.path
    if (!workflow.isExpression(componentName, 'path')) {
      path = `'${path}'`
    }
    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`
            SELECT
                *,
                JSON_EXTRACT(
                    ${params.json},
                    ${path}
                ) AS ${params.newcol}
            FROM
                ${connection.quoteName(params.source as string)}`
      case Provider.Snowflake:
        return dedent`
            SELECT
                *,
                JSON_EXTRACT_PATH_TEXT(
                    ${params.json},
                    ${path}
                ) AS ${params.newcol}
            FROM
                ${connection.quoteName(params.source as string)}`
      case Provider.Postgres:
        return dedent`
            SELECT
                *,
                JSONB_PATH_QUERY(
                    ${params.json}::jsonb,
                    ${path}::jsonpath
                ) AS ${params.newcol}
            FROM
                ${connection.quoteName(params.source as string)}`
      case Provider.Redshift: {
        //TODO: for now, no support for variables in the path parameter in RS
        const rspath = (params.path as string)
          .split(',')
          .map((p) => `'${p.trim()}'`)
          .join(',')
        return dedent`
            SELECT
                *,
                JSON_EXTRACT_PATH_TEXT(
                    ${params.json},
                    ${rspath}
                ) AS ${params.newcol}
            FROM
                ${connection.quoteName(params.source as string)}`
      }
      case Provider.DatabricksRest:
        return dedent`
            SELECT /*+ REPARTITION(40) */
                *,
                GET_JSON_OBJECT(
                    ${params.json},
                    '${params.path}'
                ) AS ${params.newcol}
            FROM
                ${connection.quoteName(params.source as string)}`
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: params.newcol as string,
      type: ColumnDataType.String
    })
  }
}

export { ExtractFromJSON }
